import { computed, inject, Injectable, signal } from '@angular/core';
import {
  BillingHistoryMonth,
  PhysicalVendor,
  Subscription,
  SubscriptionPlan,
  UsageHistoryElement,
  Vendor,
  VendorQuotaUpdate,
  VirtualVendor,
  VirtualVendorQuotaUpdate,
} from '@api/models/dashboard';
import {
  BillingHistoryData,
  PhysicalVendorsData,
  SubscriptionPlansData,
  SubscriptionsData,
  UsageData,
  VendorsData,
  VirtualVendorsData,
} from '@shared/services/dashboard';
import { LogsData } from '@shared/services/logs';
import { UsersData } from '@shared/services/users/users-data';
import { UserStore } from '@stores/user-store';
import { catchError, EMPTY, finalize, forkJoin, Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DashboardStore {
  private readonly vendorsData = inject(VendorsData);
  private readonly virtualVendorsData = inject(VirtualVendorsData);
  private readonly physicalVendorsData = inject(PhysicalVendorsData);
  private readonly subscriptionsData = inject(SubscriptionsData);
  private readonly subscriptionPlansData = inject(SubscriptionPlansData);
  private readonly billingHistoryData = inject(BillingHistoryData);
  private readonly usageData = inject(UsageData);
  private readonly usersData = inject(UsersData);
  private readonly userStore = inject(UserStore);
  private readonly logs = inject(LogsData);

  // State signals
  private readonly _vendors = signal<Vendor[]>([]);
  private readonly _billingHistory = signal<BillingHistoryMonth[]>([]);
  private readonly _usageHistory = signal<UsageHistoryElement[]>([]);

  // Loading states
  private readonly _isLoadingVendors = signal(false);
  private readonly _isLoadingBillingHistory = signal(false);
  private readonly _isLoadingUsageHistory = signal(false);
  private readonly _isUpdatingQuotas = signal(false);

  // Public readonly signals
  readonly vendors = this._vendors.asReadonly();
  readonly billingHistory = this._billingHistory.asReadonly();
  readonly usageHistory = this._usageHistory.asReadonly();

  readonly isLoadingVendors = this._isLoadingVendors.asReadonly();
  readonly isLoadingBillingHistory = this._isLoadingBillingHistory.asReadonly();
  readonly isLoadingUsageHistory = this._isLoadingUsageHistory.asReadonly();
  readonly isUpdatingQuotas = this._isUpdatingQuotas.asReadonly();

  // Virtual store vendors slice
  private readonly _virtualVendors = signal<VirtualVendor[]>([]);
  private readonly _isLoadingVirtualVendors = signal(false);
  private readonly _isUpdatingVirtualQuotas = signal(false);

  readonly virtualVendors = this._virtualVendors.asReadonly();
  readonly isLoadingVirtualVendors = this._isLoadingVirtualVendors.asReadonly();
  readonly isUpdatingVirtualQuotas = this._isUpdatingVirtualQuotas.asReadonly();

  // Physical store vendors slice (subscription model)
  private readonly _physicalVendors = signal<PhysicalVendor[]>([]);
  private readonly _isLoadingPhysicalVendors = signal(false);
  private readonly _isMutatingSubscription = signal(false);

  readonly physicalVendors = this._physicalVendors.asReadonly();
  readonly isLoadingPhysicalVendors = this._isLoadingPhysicalVendors.asReadonly();
  readonly isMutatingSubscription = this._isMutatingSubscription.asReadonly();

  // Subscription plans catalog (physical store pricing)
  private readonly _subscriptionPlans = signal<SubscriptionPlan[]>([]);
  private readonly _isLoadingSubscriptionPlans = signal(false);
  private readonly _subscriptionPlansError = signal(false);

  readonly isLoadingSubscriptionPlans = this._isLoadingSubscriptionPlans.asReadonly();
  readonly subscriptionPlansError = this._subscriptionPlansError.asReadonly();

  // Cheapest plan price (V1: single plan). Null when unknown: not loaded yet, empty list, or failed load.
  readonly subscriptionMonthlyPrice = computed<number | null>(() => {
    const plans = this._subscriptionPlans();
    if (plans.length === 0) return null;
    return Math.min(...plans.map((plan) => plan.price));
  });

  readonly isLoading = computed(
    () => this._isLoadingVendors() || this._isLoadingBillingHistory() || this._isLoadingUsageHistory()
  );

  // canManageVideoQuotas from user permissions
  readonly canManageVideoQuotas = computed(() => this.userStore.read()?.permissions.canManageVideoQuotas ?? false);

  // Fournier Retail store managers (annuaire "group") cannot activate a subscription themselves:
  // they request it through a Desk ticket, which a billing manager then processes from Evoq.
  // Hence the role check: billing managers and super admins keep the activation.
  readonly activationRequiresDeskTicket = computed(() => {
    const user = this.userStore.read();
    return user?.role === 'STORE_MANAGER' && user?.annuaire === 'group';
  });

  loadVendors(storeId: string): void {
    this._isLoadingVendors.set(true);
    this.vendorsData
      .fetchVendors(storeId)
      .pipe(
        tap((vendors) => {
          this._vendors.set(vendors);
          this.logs.info('💽 DashboardStore', 'Vendors loaded', vendors);
        }),
        catchError((error) => {
          this.logs.error('💽 DashboardStore', 'Failed to load vendors', error);
          return EMPTY;
        }),
        finalize(() => this._isLoadingVendors.set(false))
      )
      .subscribe();
  }

  loadBillingHistory(storeId: string): void {
    this._isLoadingBillingHistory.set(true);
    this.billingHistoryData
      .fetchBillingHistory(storeId)
      .pipe(
        tap((months) => {
          this._billingHistory.set(months);
          this.logs.info('💽 DashboardStore', 'Billing history loaded', months);
        }),
        catchError((error) => {
          this.logs.error('💽 DashboardStore', 'Failed to load billing history', error);
          return EMPTY;
        }),
        finalize(() => this._isLoadingBillingHistory.set(false))
      )
      .subscribe();
  }

  loadUsageHistory(storeId: string, period: { year: number; month: number }): void {
    this._isLoadingUsageHistory.set(true);
    this.usageData
      .fetchUsageHistory(storeId, period)
      .pipe(
        tap((elements) => {
          this._usageHistory.set(elements);
          if (elements.some((e) => e.totalPrice == null)) {
            this.logs.warn(
              '💽 DashboardStore',
              'Usage history: missing totalPrice on some rows, cost hidden',
              elements
            );
          }
          this.logs.info('💽 DashboardStore', 'Usage history loaded', elements);
        }),
        catchError((error) => {
          this.logs.error('💽 DashboardStore', 'Failed to load usage history', error);
          return EMPTY;
        }),
        finalize(() => this._isLoadingUsageHistory.set(false))
      )
      .subscribe();
  }

  resetStoreData(): void {
    this._vendors.set([]);
    this._virtualVendors.set([]);
    this._physicalVendors.set([]);
    this._billingHistory.set([]);
    this._usageHistory.set([]);
    this.logs.info('💽 DashboardStore', 'Store-scoped data reset');
  }

  reset(): void {
    this.resetStoreData();
    this.logs.info('💽 DashboardStore', 'Full store reset');
  }

  updateVendorQuotas(storeId: string, updates: VendorQuotaUpdate[]): Observable<Vendor[]> {
    this._isUpdatingQuotas.set(true);
    return this.vendorsData.updateQuotas(storeId, updates).pipe(
      tap((updatedVendors) => {
        const updatedMap = new Map(updatedVendors.map((v) => [v.id, v]));
        this._vendors.update((current) => current.map((v) => updatedMap.get(v.id) ?? v));
        this.logs.info('💽 DashboardStore', 'Vendor quotas updated', updatedVendors);
        this.refreshCurrentUserIfUpdated(updatedVendors);
      }),
      catchError((error) => {
        this.logs.error('💽 DashboardStore', 'Failed to update vendor quotas', error);
        throw error;
      }),
      finalize(() => this._isUpdatingQuotas.set(false))
    );
  }

  loadVirtualVendors(storeId: string): void {
    this._isLoadingVirtualVendors.set(true);
    this.virtualVendorsData
      .fetchVirtualVendors(storeId)
      .pipe(
        tap((vendors) => {
          this._virtualVendors.set(vendors);
          this.logs.info('💽 DashboardStore', 'Virtual vendors loaded', vendors);
        }),
        catchError((error) => {
          this.logs.error('💽 DashboardStore', 'Failed to load virtual vendors', error);
          return EMPTY;
        }),
        finalize(() => this._isLoadingVirtualVendors.set(false))
      )
      .subscribe();
  }

  updateVirtualVendorQuotas(storeId: string, updates: VirtualVendorQuotaUpdate[]): Observable<VirtualVendor[]> {
    this._isUpdatingVirtualQuotas.set(true);
    return this.virtualVendorsData.updateVirtualQuotas(storeId, updates).pipe(
      tap((updatedVendors) => {
        const updatedMap = new Map(updatedVendors.map((v) => [v.id, v]));
        this._virtualVendors.update((current) => current.map((v) => updatedMap.get(v.id) ?? v));
        this.logs.info('💽 DashboardStore', 'Virtual vendor quotas updated', updatedVendors);
        this.refreshCurrentUserIfUpdated(updatedVendors);
      }),
      catchError((error) => {
        this.logs.error('💽 DashboardStore', 'Failed to update virtual vendor quotas', error);
        throw error;
      }),
      finalize(() => this._isUpdatingVirtualQuotas.set(false))
    );
  }

  loadPhysicalVendors(storeId: string): void {
    this._isLoadingPhysicalVendors.set(true);
    this.physicalVendorsData
      .fetchPhysicalVendors(storeId)
      .pipe(
        tap((vendors) => {
          this._physicalVendors.set(vendors);
          this.logs.info('💽 DashboardStore', 'Physical vendors loaded', vendors);
        }),
        catchError((error) => {
          this.logs.error('💽 DashboardStore', 'Failed to load physical vendors', error);
          return EMPTY;
        }),
        finalize(() => this._isLoadingPhysicalVendors.set(false))
      )
      .subscribe();
  }

  loadSubscriptionPlans(): void {
    this._isLoadingSubscriptionPlans.set(true);
    this._subscriptionPlansError.set(false);
    this.subscriptionPlansData
      .fetchSubscriptionPlans()
      .pipe(
        tap((plans) => {
          this._subscriptionPlans.set(plans);
          this.logs.info('💽 DashboardStore', 'Subscription plans loaded', plans);
        }),
        catchError((error) => {
          this._subscriptionPlansError.set(true);
          this.logs.error('💽 DashboardStore', 'Failed to load subscription plans', error);
          return EMPTY;
        }),
        finalize(() => this._isLoadingSubscriptionPlans.set(false))
      )
      .subscribe();
  }

  activateSubscription(storeId: string, userId: string): Observable<Subscription> {
    return this.mutateSubscription(this.subscriptionsData.activate(storeId, userId), storeId);
  }

  cancelSubscription(storeId: string, userId: string): Observable<Subscription> {
    return this.mutateSubscription(this.subscriptionsData.cancel(storeId, userId), storeId);
  }

  reactivateSubscription(storeId: string, userId: string): Observable<Subscription> {
    return this.mutateSubscription(this.subscriptionsData.reactivate(storeId, userId), storeId);
  }

  // Bulk activation = N unitary POSTs (no batch endpoint); partial failure is acceptable, so the
  // list is re-fetched on completion regardless of outcome to reflect the real server state.
  activateSubscriptions(storeId: string, userIds: string[]): Observable<Subscription[]> {
    this._isMutatingSubscription.set(true);
    return forkJoin(userIds.map((userId) => this.subscriptionsData.activate(storeId, userId))).pipe(
      tap((subscriptions) => this.logs.info('💽 DashboardStore', 'Subscriptions activated in bulk', subscriptions)),
      finalize(() => {
        this._isMutatingSubscription.set(false);
        this.loadPhysicalVendors(storeId);
      })
    );
  }

  private mutateSubscription(operation$: Observable<Subscription>, storeId: string): Observable<Subscription> {
    this._isMutatingSubscription.set(true);
    return operation$.pipe(
      tap((subscription) => this.logs.info('💽 DashboardStore', 'Subscription mutated', subscription)),
      finalize(() => {
        this._isMutatingSubscription.set(false);
        this.loadPhysicalVendors(storeId);
      })
    );
  }

  private refreshCurrentUserIfUpdated(updatedVendors: { id: string }[]): void {
    const currentUserId = this.userStore.read()?.userId;
    if (!currentUserId) return;

    const currentUserWasUpdated = updatedVendors.some((v) => v.id === currentUserId);
    if (currentUserWasUpdated) {
      this.logs.info('💽 DashboardStore', 'Current user quota was updated, refreshing user data');
      this.usersData.fetchUserAccount().subscribe();
    }
  }
}
